// =============================================
// ZoneRowActions — akcje inline w "Domeny w CF" dla jednej zony:
//   * "Ustaw NS w AfterMarket" — gdy zona pending i domena jest w panelu
//     (nie not_on_account); nsGet → NsConfirmDialog (old → new) → nsSet → log.
//     Workspace z wiersza websites (X-Workspace); Sponsoraki tylko z flaga
//     (useTransferDomains juz je odfiltrowal bez flagi — tu brak domeny = brak akcji).
//   * "Podepnij hosting" — gdy sprawdzono rekordy i brak A @; IP z mapowania
//     (edytowalne) + proxied → pointDomainToIp → log.
// =============================================
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Route, Server } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { TransferDomain } from "@/services/cloudflare/transferTypes";
import { nsGet, nsSet, normalizeDomain } from "@/services/aftermarketService";
import { pointDomainToIp, isValidIPv4, type CfZoneWithAccount } from "@/services/cloudflareService";
import { insertDnsChangeLog } from "@/services/cloudflare/dnsChangeLogService";
import { NsConfirmDialog, type NsConfirmRow } from "@/components/cloudflare/transfer/NsConfirmDialog";
import type { ZoneRecordsInfo } from "./ZonesTab";

interface Props {
  zone: CfZoneWithAccount;
  records: ZoneRecordsInfo | undefined;
  accounts: CloudflareAccount[];
  findDomain: (key: string) => TransferDomain | null;
  /** po udanym "Podepnij hosting" — zeby tabela odswiezyla kolumne A */
  onRecordsChanged?: (zoneId: string, info: ZoneRecordsInfo) => void;
}

export const ZoneRowActions = ({ zone, records, accounts, findDomain, onRecordsChanged }: Props) => {
  const { toast } = useToast();
  const key = normalizeDomain(zone.name);
  const domain = findDomain(key);
  const token = accounts.find((a) => a.id === zone.cfAccountId)?.api_token;

  // --- Ustaw NS ---
  const [nsBusy, setNsBusy] = useState(false);
  const [nsRow, setNsRow] = useState<NsConfirmRow | null>(null);
  const canSetNs = zone.status !== "active" && !!domain && domain.expirySource !== "not_on_account" && !!zone.name_servers?.length;

  const startSetNs = async () => {
    if (!domain || !zone.name_servers?.length) return;
    setNsBusy(true);
    try {
      const current = await nsGet(key, domain.workspace);
      setNsRow({ domain: key, currentNs: current, newNs: zone.name_servers });
    } catch (e) {
      toast({ title: "Nie udało się pobrać obecnych NS", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setNsBusy(false);
    }
  };

  const confirmSetNs = async () => {
    if (!domain || !nsRow || nsRow.newNs === "FROM_CREATED_ZONE") return;
    const target = nsRow.newNs;
    setNsBusy(true);
    try {
      const ok = await nsSet(key, target, domain.workspace);
      await insertDnsChangeLog({
        website_id: domain.websiteId, domain: key, action: "ns_set", cf_account_id: zone.cfAccountId, workspace: domain.workspace,
        before: { ns: nsRow.currentNs }, after: { ns: target }, status: ok ? "success" : "failed",
        error: ok ? null : "AfterMarket nie potwierdził zmiany",
      });
      if (ok) toast({ title: "Nameservery zmienione ✓", description: `${key} → ${target.join(", ")}. Propagacja do 24h.` });
      else toast({ title: "AfterMarket nie potwierdził zmiany", variant: "destructive" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      await insertDnsChangeLog({
        website_id: domain.websiteId, domain: key, action: "ns_set", cf_account_id: zone.cfAccountId, workspace: domain.workspace,
        before: { ns: nsRow.currentNs }, after: { ns: target }, status: "failed", error: msg,
      });
      toast({ title: "Zmiana NS nie powiodła się", description: msg, variant: "destructive" });
    } finally {
      setNsBusy(false);
      setNsRow(null);
    }
  };

  // --- Podepnij hosting ---
  const [ipOpen, setIpOpen] = useState(false);
  const [ip, setIp] = useState("");
  const [proxied, setProxied] = useState(true);
  const [ipBusy, setIpBusy] = useState(false);
  const canPoint = !!records && !records.error && !records.aRoot && !!token;

  const openPoint = () => {
    setIp(domain?.hostingIp ?? domain?.candidateIps?.[0] ?? "");
    setProxied(true);
    setIpOpen(true);
  };

  const confirmPoint = async () => {
    if (!token) return;
    const clean = ip.trim();
    if (!isValidIPv4(clean)) { toast({ title: "Niepoprawny adres IPv4", variant: "destructive" }); return; }
    setIpBusy(true);
    const ws = domain?.workspace ?? "firma";
    try {
      const res = await pointDomainToIp(token, zone.id, zone.name, clean, proxied);
      await insertDnsChangeLog({
        website_id: domain?.websiteId ?? null, domain: key, action: "a_record", cf_account_id: zone.cfAccountId, workspace: ws,
        before: null, after: { ip: clean, proxied, root: res.root.action, www: res.www.action }, status: "success", error: null,
      });
      onRecordsChanged?.(zone.id, { aRoot: clean, aWww: clean });
      toast({ title: "Hosting podpięty ✓", description: `${zone.name}: @ ${res.root.action}, www ${res.www.action} → ${clean}` });
      setIpOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      await insertDnsChangeLog({
        website_id: domain?.websiteId ?? null, domain: key, action: "a_record", cf_account_id: zone.cfAccountId, workspace: ws,
        before: null, after: { ip: clean, proxied }, status: "failed", error: msg,
      });
      toast({ title: "Nie udało się dodać rekordów A", description: msg, variant: "destructive" });
    } finally {
      setIpBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      {canSetNs && (
        <Button variant="outline" size="sm" className="h-7" onClick={() => void startSetNs()} disabled={nsBusy} title="Przepisz NS tej domeny w AfterMarket na NS zony">
          {nsBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Route className="h-3 w-3 mr-1" />}Ustaw NS
        </Button>
      )}
      {canPoint && (
        <Button variant="outline" size="sm" className="h-7" onClick={openPoint} title="Dodaj rekordy A (@ + www) na IP hostingu">
          <Server className="h-3 w-3 mr-1" />Podepnij hosting
        </Button>
      )}

      <NsConfirmDialog
        open={!!nsRow}
        onOpenChange={(o) => { if (!o) setNsRow(null); }}
        onConfirm={() => void confirmSetNs()}
        rows={nsRow ? [nsRow] : []}
        confirming={nsBusy}
      />

      <Dialog open={ipOpen} onOpenChange={(o) => { if (!ipBusy) setIpOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Podepnij hosting — {zone.name}</DialogTitle>
            <DialogDescription>Rekordy A dla <span className="font-mono">@</span> i <span className="font-mono">www</span>. Istniejące A o tej nazwie zostaną nadpisane; CNAME nie są ruszane.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`ip-${zone.id}`}>Adres IPv4</Label>
              <Input id={`ip-${zone.id}`} value={ip} onChange={(e) => setIp(e.target.value)} placeholder="np. 185.x.x.x" className={`font-mono ${ip && !isValidIPv4(ip) ? "border-red-500" : ""}`} />
              {domain?.candidateIps && domain.candidateIps.length > 1 && (
                <p className="text-xs text-muted-foreground">Kilka hostingów: {domain.candidateIps.join(", ")}</p>
              )}
              {!domain?.hostingIp && !domain?.candidateIps?.length && (
                <p className="text-xs text-muted-foreground">Brak IP w mapowaniu domena → hosting; wpisz ręcznie.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch id={`px-${zone.id}`} checked={proxied} onCheckedChange={setProxied} />
              <Label htmlFor={`px-${zone.id}`}>Proxy Cloudflare (pomarańczowa chmurka)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIpOpen(false)} disabled={ipBusy}>Anuluj</Button>
            <Button onClick={() => void confirmPoint()} disabled={ipBusy || !isValidIPv4(ip)}>
              {ipBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Dodaj rekordy A
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
